import { AfterViewInit, Component, ElementRef, OnDestroy, input, signal, viewChild } from '@angular/core';
import { BELOW_TOP_MENU, isUnderTopMenu } from 'app/framework/top-menu-scroll';

const CONDENSED_CLASS = 'container-header--condensed';
const MEASURING_CLASS = 'container-header--measuring';
const NARROW_SCREEN = '(max-width: 599.98px)';

/** Default content renders as the filters; elements marked `pageHeaderActions` render as the actions. */
@Component({
  selector: 'app-page-header',
  templateUrl: './page-header.component.html',
  styleUrls: ['./page-header.component.scss'],
  standalone: false,
  host: { '[class.page-header--stacked]': 'stacked()' },
})
export class PageHeaderComponent implements AfterViewInit, OnDestroy {
  public readonly title = input.required<string>();
  public readonly description = input('');
  /** Defaults to the title. */
  public readonly topMenuTitle = input('');
  public readonly shadow = input(true);
  public readonly stickyOnPhone = input(true);

  protected readonly condensed = signal(false);
  protected readonly stacked = signal(false);

  private readonly header = viewChild.required<ElementRef<HTMLElement>>('header');
  private readonly scrollMarker = viewChild.required<ElementRef<HTMLElement>>('scrollMarker');
  private readonly titleBlock = viewChild.required<ElementRef<HTMLElement>>('titleBlock');
  private readonly filters = viewChild.required<ElementRef<HTMLElement>>('filters');
  private readonly actions = viewChild.required<ElementRef<HTMLElement>>('actions');
  private readonly narrowScreen = window.matchMedia(NARROW_SCREEN);

  private scrollObserver: IntersectionObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private oneRowWidth = 0;

  public get element(): HTMLElement {
    return this.header().nativeElement;
  }

  /** The header's height once the page has scrolled far enough to tighten it. */
  public scrolledHeight(): number {
    const header = this.element;
    if (this.condensed()) {
      return header.offsetHeight;
    }

    header.classList.add(MEASURING_CLASS, CONDENSED_CLASS);
    const height = header.offsetHeight;
    header.classList.remove(CONDENSED_CLASS);
    // Lays the full height out again while transitions are still off, so restoring it doesn't animate.
    header.getBoundingClientRect();
    header.classList.remove(MEASURING_CLASS);
    return height;
  }

  ngAfterViewInit(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.updateStacked());
      for (const part of [this.header(), this.titleBlock(), this.filters(), this.actions()]) {
        this.resizeObserver.observe(part.nativeElement);
      }
    }

    if (typeof IntersectionObserver !== 'undefined') {
      this.scrollObserver = new IntersectionObserver((entries) => this.updateCondensed(entries[entries.length - 1]), {
        ...BELOW_TOP_MENU,
        threshold: [0, 1],
      });
      this.scrollObserver.observe(this.scrollMarker().nativeElement);
    }
  }

  ngOnDestroy(): void {
    this.scrollObserver?.disconnect();
    this.resizeObserver?.disconnect();
  }

  // Remembers how wide the one-row layout needs to be, so a growing window only unstacks once it fits.
  private updateStacked(): void {
    const header = this.element;
    const width = header.clientWidth;
    if (this.narrowScreen.matches) {
      this.stacked.set(true);
    } else if (!this.stacked()) {
      if (header.scrollWidth > width) {
        this.oneRowWidth = header.scrollWidth;
        this.stacked.set(true);
      }
    } else if (width >= this.oneRowWidth) {
      this.stacked.set(false);
    }
  }

  // Separate tighten and widen points, so the height change can't flip the state back and forth.
  private updateCondensed(entry: IntersectionObserverEntry): void {
    if (isUnderTopMenu(entry)) {
      this.condensed.set(true);
    } else if (entry.intersectionRatio === 1) {
      this.condensed.set(false);
    }
  }
}
